import { ApolloClient, InMemoryCache, gql } from '@apollo/client';
import { GetServerSidePropsContext } from 'next';

import { normalizePathname } from './config';
import {
  fetchWebflowIntegrationPublicConfig,
  isWebflowFeatureEnabledForRequest,
  WebflowNavigationChrome,
} from './fetch-public-config';
import {
  extractWebflowContent,
  prepareWebflowPageAssets,
  WebflowExtractedPage,
} from './extract-content';
import { fetchWebflowHtml } from './fetch-webflow-html';
import { shouldProxyToWebflow } from './reserved-routes';

const TENANT_FOR_WEBFLOW_FOOTER = gql`
  query TenantForWebflowFooter {
    tenant {
      result {
        _id
        displayName
        logo {
          _id
          imageProxy(input: {})
        }
        contactInfo {
          phoneNumber
          email
          addressLine0
          addressLocality
          addressAdministrativeArea
          addressRegionCode
          addressPostalCode
          linkedin
          youtube
          twitter
          instagram
          facebook
        }
        privacyPolicyLink {
          url
          text
        }
        termsAndConditionsLink {
          url
          text
        }
        websiteCopyrightDate
      }
    }
  }
`;

const WEBFLOW_PATH_ACCESS_QUERY = gql`
  query WebflowIntegrationAssertPathAccess($path: String!) {
    webflowIntegrationAssertPathAccess(path: $path) {
      allowed
    }
  }
`;

export type WebflowHybridPageProps = {
  webflow: WebflowExtractedPage;
  navigationChrome: WebflowNavigationChrome;
  tenant: {
    _id: string;
    displayName: string;
    logo?: {
      _id: string;
      imageProxy?: string | null;
    } | null;
    contactInfo?: Record<string, unknown> | null;
    privacyPolicyLink?: { url: string; text: string } | null;
    termsAndConditionsLink?: { url: string; text: string } | null;
    websiteCopyrightDate?: string | number | null;
  } | null;
};

const buildUpstreamUrl = (
  publishOrigin: string,
  pathname: string,
  search: string
): string => {
  const originUrl = new URL(publishOrigin);
  const upstreamPath = pathname === '/' ? '/' : pathname;

  return new URL(`${upstreamPath}${search}`, originUrl.origin).toString();
};

const getApolloSsrClient = () =>
  new ApolloClient({
    ssrMode: true,
    uri: process.env.NEXT_PUBLIC_APOLLO_HTTP_URI,
    cache: new InMemoryCache(),
  });

const getTenantAuthHeaders = (context: GetServerSidePropsContext) => ({
  tenant_domain:
    context.req.headers.tenant_domain ||
    context.req.headers['x-forwarded-host'] ||
    context.req.headers.host,
  authorization: `Bearer ${context.req.cookies['accounts%3AaccessToken']}`,
});

const getRequestPathname = (context: GetServerSidePropsContext): string => {
  const [pathnameWithSearch] = (context.resolvedUrl || '/').split('#');
  const [rawPathname] = pathnameWithSearch.split('?');
  return normalizePathname(rawPathname);
};

const getUpstreamPathnameFromContext = (
  context: GetServerSidePropsContext
): { pathname: string; query: string } => {
  const [pathnameWithSearch] = (context.resolvedUrl || '/').split('#');
  const [rawPathname, search = ''] = pathnameWithSearch.split('?');

  const slugParam = context.params?.slug;
  if (slugParam) {
    const slugPath = Array.isArray(slugParam)
      ? `/${slugParam.join('/')}`
      : `/${slugParam}`;

    return {
      pathname: normalizePathname(slugPath),
      query: search ? `?${search}` : '',
    };
  }

  return {
    pathname: normalizePathname(rawPathname),
    query: search ? `?${search}` : '',
  };
};

const assertWebflowPathAccess = async (
  context: GetServerSidePropsContext,
  path: string
): Promise<boolean> => {
  if (!process.env.NEXT_PUBLIC_APOLLO_HTTP_URI) {
    return true;
  }

  try {
    const client = getApolloSsrClient();
    const query = await client.query({
      query: WEBFLOW_PATH_ACCESS_QUERY,
      variables: { path },
      context: {
        headers: getTenantAuthHeaders(context),
      },
      fetchPolicy: 'no-cache',
    });

    return (
      query.data?.webflowIntegrationAssertPathAccess?.allowed !== false
    );
  } catch (error) {
    // Unknown tenant / bot traffic — treat as no access without noisy logs.
    if (
      (error as { networkError?: { statusCode?: number } })?.networkError
        ?.statusCode === 422
    ) {
      return false;
    }
    console.error('Failed to assert Webflow path access', { path, error });
    return false;
  }
};

const fetchTenantForFooter = async (
  context: GetServerSidePropsContext
): Promise<WebflowHybridPageProps['tenant']> => {
  try {
    const client = getApolloSsrClient();
    const query = await client.query({
      query: TENANT_FOR_WEBFLOW_FOOTER,
      context: {
        headers: getTenantAuthHeaders(context),
      },
      fetchPolicy: 'no-cache',
    });
    return query.data?.tenant?.result ?? null;
  } catch (error) {
    if (
      (error as { networkError?: { statusCode?: number } })?.networkError
        ?.statusCode !== 422
    ) {
      console.error('Failed to load tenant for Webflow footer', error);
    }
    return null;
  }
};

export type GetWebflowHybridPagePropsOptions = {
  /**
   * Set when the caller already read `featureFlags.webflow === true` from
   * the tenant on its own CMS query (homepage / CMS page SSR). Skips the
   * standalone Webflow flag GraphQL call so flag-off tenants never touch
   * any Webflow code path — their CMS flow runs exactly as it did before
   * the Webflow integration existed.
   */
  skipFeatureFlagCheck?: boolean;
};

/**
 * Load a static/marketing Webflow page.
 * Extract mode and chrome follow tenant navigationChrome setting.
 */
export const getWebflowHybridPageProps = async (
  context: GetServerSidePropsContext,
  options?: GetWebflowHybridPagePropsOptions
): Promise<
  | { kind: 'ok'; props: WebflowHybridPageProps }
  | { kind: 'skip' }
  | { kind: 'notFound' }
  | { kind: 'error'; statusCode: number }
> => {
  const requestPath = getRequestPathname(context);
  const { pathname: upstreamPath, query } =
    getUpstreamPathnameFromContext(context);

  // Avoid GraphQL/Webflow work for reserved TW routes (favicon, assets, app paths).
  if (
    !requestPath.startsWith('/site/') &&
    requestPath !== '/' &&
    !shouldProxyToWebflow(requestPath)
  ) {
    return { kind: 'skip' };
  }

  // Do not call any Webflow GraphQL/upstream APIs unless the tenant flag is on.
  if (
    !options?.skipFeatureFlagCheck &&
    !(await isWebflowFeatureEnabledForRequest(context))
  ) {
    return { kind: 'skip' };
  }

  const config = await fetchWebflowIntegrationPublicConfig(context);

  if (!config?.enabled || !config.publishOrigin) {
    return { kind: 'skip' };
  }

  const navigationChrome = config.navigationChrome;

  const accessPath =
    requestPath.startsWith('/site/') || requestPath === '/'
      ? requestPath
      : upstreamPath;

  if (!(await assertWebflowPathAccess(context, accessPath))) {
    return { kind: 'notFound' };
  }

  if (!shouldProxyToWebflow(upstreamPath) && !requestPath.startsWith('/site/')) {
    return { kind: 'skip' };
  }

  const targetUrl = buildUpstreamUrl(config.publishOrigin, upstreamPath, query);
  const originUrl = new URL(config.publishOrigin);

  let upstream: { ok: boolean; status: number; html: string };

  try {
    upstream = await fetchWebflowHtml(targetUrl, originUrl.host);
  } catch (error) {
    console.error('Webflow hybrid fetch failed', { targetUrl, error });
    return { kind: 'error', statusCode: 502 };
  }

  if (upstream.status === 404) {
    return { kind: 'notFound' };
  }

  if (!upstream.ok) {
    console.error('Webflow hybrid upstream error', {
      targetUrl,
      status: upstream.status,
    });
    return { kind: 'error', statusCode: upstream.status };
  }

  const extractMode = navigationChrome === 'TRADEWING' ? 'content' : 'full';
  const extracted = extractWebflowContent(
    upstream.html,
    config.publishOrigin,
    extractMode
  );

  if (!extracted) {
    console.error(
      extractMode === 'content'
        ? `Webflow page missing [data-tradewing-content] marker: ${targetUrl}`
        : `Failed to extract Webflow page body: ${targetUrl}`
    );
    return { kind: 'notFound' };
  }

  const preparedAssets = await prepareWebflowPageAssets(extracted.assets);
  const tenant =
    navigationChrome === 'TRADEWING'
      ? await fetchTenantForFooter(context)
      : null;

  if (navigationChrome === 'TRADEWING' && !tenant) {
    return { kind: 'error', statusCode: 422 };
  }

  return {
    kind: 'ok',
    props: {
      webflow: {
        ...extracted,
        assets: preparedAssets,
      },
      navigationChrome,
      tenant,
    },
  };
};
